import React, { useEffect, useState, useRef, useCallback } from "react";
import { Link, useParams, useLocation } from "react-router-dom";
import { db } from "../firebase.config";
import {
  collection,
  getDocs,
  query,
  where,
  limit,
  startAfter,
  orderBy,
} from "firebase/firestore";
import ScaleSpinner from "../components/feedback/ScaleSpinner";
import ListingItem from "../components/listings/ListingItem";
import TopFilterBar from "../components/sidebar/TopFilterBar";
import { Helmet } from "react-helmet";
import { 
  Button, 
  Alert, 
  AlertIcon, 
  AlertTitle, 
  AlertDescription,
  Badge,
  Switch,
  FormControl,
  FormLabel,
  Collapse,
  Icon,
  Input,
  Select,
  VStack,
  Text,
  Box,
  Flex,
  Divider,
  Tag,
  TagLabel,
  TagCloseButton
} from "@chakra-ui/react";
import { 
  FiFilter, 
  FiHome, 
  FiShoppingBag, 
  FiEye,
  FiX,
  FiUsers,
} from "react-icons/fi";
import { 
  GiMoneyStack, 
} from "react-icons/gi";
import { 
  FaExchangeAlt, 
  FaRoute,
  FaClock,
  FaLuggageCart 
} from "react-icons/fa";
import debounce from "lodash/debounce";

const ITEMS_PER_PAGE = 10;

// Pakistan major cities for ride filtering
const pakistanCities = [
  "Karachi", "Lahore", "Islamabad", "Rawalpindi", "Faisalabad",
  "Multan", "Peshawar", "Quetta", "Gujranwala", "Sialkot",
  "Hyderabad", "Abbottabad", "Bahawalpur", "Sargodha", "Sukkur", 
  "Swat", "Gilgit", "Skardu", "Muzaffarabad", "Mardan", "Taxila", 
  "Dir", "Chitral", "Nowshehra", "Swabi", "Malakand"
];

const Listings = () => {
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [homeUsedCars, setHomeUsedCars] = useState([]);
  const [showroomListings, setShowroomListings] = useState([]);
  const [lastVisible, setLastVisible] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [listingType, setListingType] = useState("all");
  const [showroomPage, setShowroomPage] = useState(1);
  const [error, setError] = useState(null);

  // Advanced filter states
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  
  // Sale/Rent specific filters
  const [installmentsAvailable, setInstallmentsAvailable] = useState(false);
  const [exchangeAvailable, setExchangeAvailable] = useState(false);
  const [maxDownPayment, setMaxDownPayment] = useState("");
  const [maxMonthlyPayment, setMaxMonthlyPayment] = useState("");
  const [minInstallmentPeriod, setMinInstallmentPeriod] = useState("");
  const [maxInstallmentPeriod, setMaxInstallmentPeriod] = useState("");
  const [exchangeManufacturer, setExchangeManufacturer] = useState("");
  const [exchangeCondition, setExchangeCondition] = useState("");
  
  // Ride sharing specific filters
  const [fromCity, setFromCity] = useState("");
  const [toCity, setToCity] = useState("");
  const [rideType, setRideType] = useState("");
  const [departureDate, setDepartureDate] = useState("");
  const [availableSeats, setAvailableSeats] = useState("");
  const [luggageSpace, setLuggageSpace] = useState("");
  const [smoking, setSmoking] = useState(false);
  const [pets, setPets] = useState(false);
  const [femaleOnly, setFemaleOnly] = useState(false);
  
  // Common filters
  const [carModel, setCarModel] = useState("");
  const [city, setCity] = useState("");
  const [region, setRegion] = useState("");
  const [priceRange, setPriceRange] = useState([0, 10000000]);
  
  // Debounced filters
  const [debouncedFilters, setDebouncedFilters] = useState({
    carModel: "",
    city: "",
    region: "",
    priceRange: [0, 10000000],
    installmentsAvailable: false,
    exchangeAvailable: false,
    maxDownPayment: "",
    maxMonthlyPayment: "",
    minInstallmentPeriod: "",
    maxInstallmentPeriod: "",
    exchangeManufacturer: "",
    exchangeCondition: "",
    fromCity: "",
    toCity: "",
    rideType: "",
    departureDate: "",
    availableSeats: "",
    luggageSpace: "",
    smoking: false,
    pets: false,
    femaleOnly: false
  });

  const observerRef = useRef();

  const params = useParams();
  const location = useLocation();
  const param = location.state || {};

  const isSaleCategory = params.categoryName === "sale";
  const isRentCategory = params.categoryName === "rent";
  const isRideCategory = params.categoryName === "ride";

  const getCategoryTitle = () => {
    switch (params.categoryName) {
      case "sale":
        return "Cars For Sale";
      case "rent":
        return "Cars For Rent";
      case "ride":
        return "Ride Sharing";
      default:
        return "Listings";
    }
  };

  const getCategoryDescription = () => {
    switch (params.categoryName) {
      case "sale":
        return "Find the best deals on cars for sale from both private sellers and showrooms.";
      case "rent":
        return "Discover premium cars available for rent across Pakistan.";
      case "ride":
        return "Find ride sharing opportunities across Pakistan. Save money and travel together.";
      default:
        return "Browse available vehicle listings.";
    }
  };

  const getCategoryIcon = () => {
    switch (params.categoryName) {
      case "sale":
        return <FiShoppingBag className="text-2xl" />;
      case "rent":
        return <FiHome className="text-2xl" />;
      case "ride":
        return <FiUsers className="text-2xl" />;
      default:
        return <FiEye className="text-2xl" />;
    }
  };

  const getCategoryColor = () => {
    switch (params.categoryName) {
      case "sale":
        return "from-blue-600 to-sky-500";
      case "rent":
        return "from-indigo-600 to-blue-500";
      case "ride":
        return "from-purple-600 to-pink-500";
      default:
        return "from-gray-600 to-gray-500";
    }
  };

  // Helper function for partial matching
  const isPartialMatch = (target, search) => {
    if (!target || !search) return false;
    const targetLower = target.toLowerCase();
    const searchLower = search.toLowerCase();
    
    if (targetLower.includes(searchLower)) return true;
    
    const searchWords = searchLower.split(/\s+/);
    const targetWords = targetLower.split(/\s+/);
    
    return searchWords.every(word => 
      targetWords.some(targetWord => targetWord.includes(word))
    );
  };

  // Calculate monthly installment
  const calculateMonthlyInstallment = (listing) => {
    if (!listing.installmentsAvailable) return 0;
    
    const principal = parseFloat(listing.price) - parseFloat(listing.downPayment || 0);
    if (principal <= 0) return 0;
    
    const monthlyRate = (parseFloat(listing.interestRate) || 5) / 100 / 12;
    const months = parseInt(listing.installmentPeriod) || 12;
    
    const monthlyPayment = (principal * monthlyRate * Math.pow(1 + monthlyRate, months)) / 
                          (Math.pow(1 + monthlyRate, months) - 1);
    
    return Math.round(monthlyPayment);
  };

  // Debounced filter update
  const debouncedUpdateFilters = useCallback(
    debounce((filters) => {
      setDebouncedFilters(filters);
    }, 500),
    []
  );

  // Update debounced filters
  useEffect(() => {
    debouncedUpdateFilters({
      carModel,
      city,
      region,
      priceRange,
      installmentsAvailable,
      exchangeAvailable,
      maxDownPayment,
      maxMonthlyPayment,
      minInstallmentPeriod,
      maxInstallmentPeriod,
      exchangeManufacturer,
      exchangeCondition,
      fromCity,
      toCity,
      rideType,
      departureDate,
      availableSeats,
      luggageSpace,
      smoking,
      pets,
      femaleOnly
    });
  }, [
    carModel, city, region, priceRange,
    installmentsAvailable, exchangeAvailable, maxDownPayment, maxMonthlyPayment,
    minInstallmentPeriod, maxInstallmentPeriod, exchangeManufacturer, exchangeCondition,
    fromCity, toCity, rideType, departureDate, availableSeats, luggageSpace,
    smoking, pets, femaleOnly, debouncedUpdateFilters
  ]);

  // FIX: filterListings now accepts filters as a parameter instead of closing over debouncedFilters
  const filterListings = (listings, filters) => {
    if (!listings || listings.length === 0) return [];
    
    const {
      carModel: filterCarModel,
      city: filterCity,
      region: filterRegion,
      priceRange: filterPriceRange,
      installmentsAvailable: filterInstallments,
      exchangeAvailable: filterExchange,
      maxDownPayment: filterMaxDownPayment,
      maxMonthlyPayment: filterMaxMonthlyPayment,
      minInstallmentPeriod: filterMinPeriod,
      maxInstallmentPeriod: filterMaxPeriod,
      exchangeManufacturer: filterExchangeMfr,
      exchangeCondition: filterExchangeCondition,
      fromCity: filterFromCity,
      toCity: filterToCity,
      rideType: filterRideType,
      departureDate: filterDepartureDate,
      availableSeats: filterAvailableSeats,
      luggageSpace: filterLuggageSpace,
      smoking: filterSmoking,
      pets: filterPets,
      femaleOnly: filterFemaleOnly
    } = filters;

    const filtered = listings.filter((listing) => {
      const priceValue = listing.data.displayPrice || listing.data.price || 0;
      const numericPrice = parseInt(priceValue) || 0;
      const monthlyPayment = listing.data.monthlyPayment || calculateMonthlyInstallment(listing.data);

      // Common filters
      const matchesCarModel = filterCarModel === "" ||
        isPartialMatch(listing.data.modelName || "", filterCarModel) ||
        isPartialMatch(listing.data.manufacturer || "", filterCarModel);

      const matchesCity = filterCity === "" ||
        listing.data.city?.toLowerCase() === filterCity.toLowerCase();

      const matchesRegion = filterRegion === "" ||
        listing.data.location?.toLowerCase().includes(filterRegion.toLowerCase());

      const matchesPriceRange = numericPrice >= filterPriceRange[0] && numericPrice <= filterPriceRange[1];

      // Sale/Rent specific filters
      const matchesInstallments = !filterInstallments || listing.data.installmentsAvailable === true;
      const matchesExchange = !filterExchange || listing.data.exchangeAvailable === true;
      const matchesDownPayment = filterMaxDownPayment === "" || (listing.data.downPayment || 0) <= parseInt(filterMaxDownPayment);
      const matchesMonthlyPayment = filterMaxMonthlyPayment === "" || monthlyPayment <= parseInt(filterMaxMonthlyPayment);
      const matchesMinPeriod = filterMinPeriod === "" || (listing.data.installmentPeriod || 0) >= parseInt(filterMinPeriod);
      const matchesMaxPeriod = filterMaxPeriod === "" || (listing.data.installmentPeriod || 0) <= parseInt(filterMaxPeriod);
      const matchesExchangeMfr = filterExchangeMfr === "" || isPartialMatch(listing.data.exchangeManufacturer || "", filterExchangeMfr);
      const matchesExchangeCondition = filterExchangeCondition === "" || listing.data.exchangeCondition === filterExchangeCondition;

      // Ride specific filters
      let matchesFromCity = true;
      let matchesToCity = true;
      let matchesRideType = true;
      let matchesDepartureDate = true;
      let matchesAvailableSeats = true;
      let matchesLuggageSpace = true;
      let matchesSmoking = true;
      let matchesPets = true;
      let matchesFemaleOnly = true;

      // Only apply ride filters if we're in ride category
      if (isRideCategory) {
        if (filterFromCity && filterFromCity !== "") {
          matchesFromCity = listing.data.fromCity && 
            listing.data.fromCity.toLowerCase() === filterFromCity.toLowerCase();
        }
        
        if (filterToCity && filterToCity !== "") {
          matchesToCity = listing.data.toCity && 
            listing.data.toCity.toLowerCase() === filterToCity.toLowerCase();
        }
        
        if (filterRideType && filterRideType !== "") {
          matchesRideType = listing.data.rideType === filterRideType;
        }
        
        if (filterDepartureDate && filterDepartureDate !== "") {
          matchesDepartureDate = listing.data.departureDate === filterDepartureDate;
        }
        
        if (filterAvailableSeats && filterAvailableSeats !== "") {
          matchesAvailableSeats = (listing.data.availableSeats || 0) >= parseInt(filterAvailableSeats);
        }
        
        if (filterLuggageSpace && filterLuggageSpace !== "") {
          matchesLuggageSpace = listing.data.luggageSpace === filterLuggageSpace;
        }
        
        if (filterSmoking) {
          matchesSmoking = listing.data.smoking === false || !listing.data.smoking;
        }
        
        if (filterPets) {
          matchesPets = listing.data.pets === true;
        }
        
        if (filterFemaleOnly) {
          matchesFemaleOnly = listing.data.femaleOnly === true;
        }
      }

      if (isRideCategory) {
        return matchesCarModel && matchesPriceRange && matchesFromCity && matchesToCity &&
               matchesRideType && matchesDepartureDate && matchesAvailableSeats &&
               matchesLuggageSpace && matchesSmoking && matchesPets && matchesFemaleOnly;
      } else {
        return matchesCarModel && matchesCity && matchesRegion && matchesPriceRange &&
               matchesInstallments && matchesExchange && matchesDownPayment &&
               matchesMonthlyPayment && matchesMinPeriod && matchesMaxPeriod &&
               matchesExchangeMfr && matchesExchangeCondition;
      }
    });
    
    return filtered;
  };

  // FIX: fetchHomeUsedCars accepts currentFilters as a parameter
  const fetchHomeUsedCars = async (isNextPage = false, currentFilters = debouncedFilters) => {
    try {
      console.log("Fetching listings for category:", params.categoryName);
      
      if (isNextPage) {
        setLoadingMore(true);
      } else {
        setLoading(true);
        setHomeUsedCars([]);
        setLastVisible(null);
        setHasMore(true);
      }

      const carsRef = collection(db, "cars");
      
      const q = query(
        carsRef,
        where("type", "==", params.categoryName),
        orderBy("timestamp", "desc")
      );
      
      const qSnap = await getDocs(q);
      console.log(`Found ${qSnap.docs.length} ${params.categoryName} listings`);
      
      const carsArray = [];

      qSnap.forEach((doc) => {
        const carData = doc.data();
        carsArray.push({
          id: doc.id,
          data: { 
            ...carData, 
            displayPrice: carData.price || 0,
            sellerId: carData.sellerId || null,
            monthlyPayment: calculateMonthlyInstallment(carData)
          },
        });
      });
      
      // FIX: pass currentFilters explicitly instead of relying on closure
      const filteredCars = filterListings(carsArray, currentFilters);
      console.log(`After filtering: ${filteredCars.length} listings`);
      
      filteredCars.sort((a, b) => {
        const timeA = a.data.timestamp?.toDate?.() || new Date(0);
        const timeB = b.data.timestamp?.toDate?.() || new Date(0);
        return timeB - timeA;
      });
      
      setHomeUsedCars(filteredCars);
      setHasMore(false);
      setLoading(false);
      setLoadingMore(false);
      
    } catch (error) {
      console.error("Error fetching listings:", error);
      setError("Failed to load listings");
      setLoading(false);
      setLoadingMore(false);
    }
  };

  // FIX: fetchShowroomListings accepts currentFilters as a parameter
  const fetchShowroomListings = async (currentFilters = debouncedFilters) => {
    if (!isSaleCategory) return;
    
    try {
      const sellersRef = collection(db, "Feature-Seller");
      const sellersSnap = await getDocs(sellersRef);

      let fetchedShowroomListings = [];

      for (const sellerDoc of sellersSnap.docs) {
        const sellerId = sellerDoc.id;
        const listingsRef = collection(db, "Feature-Seller", sellerId, "listings");
        const listingsSnap = await getDocs(listingsRef);

        const sellerListings = listingsSnap.docs.map((doc) => {
          const listingData = doc.data();
          return {
            id: doc.id,
            type: "showroom",
            data: {
              ...listingData,
              sellerId,
              isShowroom: true,
              displayPrice: listingData.price || 0,
              sellerInfo: { ...sellerDoc.data(), verified: true },
              monthlyPayment: calculateMonthlyInstallment(listingData)
            },
          };
        });

        fetchedShowroomListings = [...fetchedShowroomListings, ...sellerListings];
      }

      // FIX: pass currentFilters explicitly instead of relying on closure
      const filteredShowroom = filterListings(fetchedShowroomListings, currentFilters);
      filteredShowroom.sort((a, b) => (b.data.isFeatured ? 1 : 0) - (a.data.isFeatured ? 1 : 0));
      
      setShowroomListings(filteredShowroom);
    } catch (err) {
      console.error("Error fetching showroom listings:", err);
    }
  };

  // Get paginated showroom listings
  const getPaginatedShowroomListings = () => {
    if (!isSaleCategory) return [];
    const itemsPerPage = 5;
    const startIndex = (showroomPage - 1) * itemsPerPage;
    return showroomListings.slice(startIndex, startIndex + itemsPerPage);
  };

  // Create blended array
  const getBlendedCars = () => {
    if (!isSaleCategory) {
      return homeUsedCars.map(car => ({ ...car, type: 'home' }));
    }
    
    const paginatedShowroom = getPaginatedShowroomListings();
    
    switch (listingType) {
      case "home":
        return homeUsedCars.map(car => ({ ...car, type: 'home' }));
      case "showroom":
        return paginatedShowroom.map(car => ({ ...car, type: 'showroom' }));
      default:
        const blendedCars = [...homeUsedCars];
        if (paginatedShowroom.length > 0) {
          const middleIndex = Math.max(2, Math.floor(blendedCars.length / 2));
          blendedCars.splice(middleIndex, 0, ...paginatedShowroom);
        }
        return blendedCars;
    }
  };

  const handleLoadMoreShowroom = () => {
    const itemsPerPage = 5;
    const totalShowroomPages = Math.ceil(showroomListings.length / itemsPerPage);
    if (showroomPage < totalShowroomPages) {
      setShowroomPage(prev => prev + 1);
    }
  };

  const handleClearFilters = () => {
    setCarModel("");
    setCity("");
    setRegion("");
    setPriceRange([0, 10000000]);
    setInstallmentsAvailable(false);
    setExchangeAvailable(false);
    setMaxDownPayment("");
    setMaxMonthlyPayment("");
    setMinInstallmentPeriod("");
    setMaxInstallmentPeriod("");
    setExchangeManufacturer("");
    setExchangeCondition("");
    setFromCity("");
    setToCity("");
    setRideType("");
    setDepartureDate("");
    setAvailableSeats("");
    setLuggageSpace("");
    setSmoking(false);
    setPets(false);
    setFemaleOnly(false);
  };

  const getActiveFilterCount = () => {
    let count = 0;
    if (carModel) count++;
    if (city) count++;
    if (region) count++;
    if (priceRange[0] > 0 || priceRange[1] < 10000000) count++;
    if (installmentsAvailable) count++;
    if (exchangeAvailable) count++;
    if (maxDownPayment) count++;
    if (maxMonthlyPayment) count++;
    if (minInstallmentPeriod) count++;
    if (maxInstallmentPeriod) count++;
    if (exchangeManufacturer) count++;
    if (exchangeCondition) count++;
    if (fromCity) count++;
    if (toCity) count++;
    if (rideType) count++;
    if (departureDate) count++;
    if (availableSeats) count++;
    if (luggageSpace) count++;
    if (smoking) count++;
    if (pets) count++;
    if (femaleOnly) count++;
    return count;
  };

  // FIX: Re-filter effect now passes debouncedFilters explicitly to both fetch functions
  useEffect(() => {
    const reFilter = async () => {
      await fetchHomeUsedCars(false, debouncedFilters);
      if (isSaleCategory) {
        await fetchShowroomListings(debouncedFilters);
      }
    };
    
    reFilter();
  }, [debouncedFilters]);

  // FIX: Initial data fetch passes explicit initialFilters object to avoid stale closure
  useEffect(() => {
    const initialize = async () => {
      const initialFilters = {
        carModel: param?.carModel || "",
        city: param?.city || "",
        region: "",
        priceRange: [0, 10000000],
        installmentsAvailable: false,
        exchangeAvailable: false,
        maxDownPayment: "",
        maxMonthlyPayment: "",
        minInstallmentPeriod: "",
        maxInstallmentPeriod: "",
        exchangeManufacturer: "",
        exchangeCondition: "",
        fromCity: param?.fromCity || "",
        toCity: param?.toCity || "",
        rideType: param?.rideType || "",
        departureDate: "",
        availableSeats: "",
        luggageSpace: "",
        smoking: false,
        pets: false,
        femaleOnly: false,
      };

      if (param?.carModel) setCarModel(param.carModel);
      if (param?.city) setCity(param.city);
      if (param?.fromCity) setFromCity(param.fromCity);
      if (param?.toCity) setToCity(param.toCity);
      if (param?.rideType) setRideType(param.rideType);
      
      await fetchHomeUsedCars(false, initialFilters);
      
      if (isSaleCategory) {
        await fetchShowroomListings(initialFilters);
      }
    };
    
    initialize();
  }, [params.categoryName]);

  // Reset on route change
  useEffect(() => {
    setCarModel(param?.carModel || "");
    setCity(param?.city || "");
    setFromCity(param?.fromCity || "");
    setToCity(param?.toCity || "");
    setRideType(param?.rideType || "");
    setRegion("");
    setPriceRange([0, 10000000]);
    setInstallmentsAvailable(false);
    setExchangeAvailable(false);
    setMaxDownPayment("");
    setMaxMonthlyPayment("");
    setMinInstallmentPeriod("");
    setMaxInstallmentPeriod("");
    setExchangeManufacturer("");
    setExchangeCondition("");
    setDepartureDate("");
    setAvailableSeats("");
    setLuggageSpace("");
    setSmoking(false);
    setPets(false);
    setFemaleOnly(false);
    setListingType("all");
    setShowroomPage(1);
    setHomeUsedCars([]);
    setShowroomListings([]);
    setLastVisible(null);
    setHasMore(true);
  }, [location.pathname]);

  const displayCars = getBlendedCars();
  const totalHomeUsed = homeUsedCars.length;
  const totalShowroom = showroomListings.length;
  const activeFilterCount = getActiveFilterCount();

  if (loading && homeUsedCars.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <ScaleSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Alert status="error" variant="subtle">
          <AlertIcon />
          <AlertTitle>{error}</AlertTitle>
        </Alert>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50/30 via-white to-white">
      <Helmet>
        <title>
          {carModel
            ? `${carModel} for ${params.categoryName} | NCPWheels`
            : `${getCategoryTitle()} | NCPWheels`}
        </title>
        <meta name="description" content={getCategoryDescription()} />
      </Helmet>

      {/* Top Filter Bar */}
      <div className={`bg-gradient-to-r ${getCategoryColor()} shadow-lg`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <TopFilterBar
            carModel={carModel}
            city={city}
            region={region}
            priceRange={priceRange}
            setCarModel={setCarModel}
            setCity={setCity}
            setRegion={setRegion}
            setPriceRange={setPriceRange}
            isRideCategory={isRideCategory}
            fromCity={fromCity}
            toCity={toCity}
            setFromCity={setFromCity}
            setToCity={setToCity}
          />
        </div>
      </div>

      {/* Advanced Filters Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <div className="bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden">
          <button
            onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
            className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
          >
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2">
                {isRideCategory ? (
                  <>
                    <FiUsers className="text-2xl text-purple-600" />
                    <FaRoute className="text-2xl text-pink-600" />
                  </>
                ) : (
                  <>
                    <GiMoneyStack className="text-2xl text-green-600" />
                    <FaExchangeAlt className="text-2xl text-purple-600" />
                  </>
                )}
              </div>
              <span className="font-semibold text-gray-800">
                {isRideCategory ? "Ride Preferences & Filters" : "Payment Options & Advanced Filters"}
              </span>
              {activeFilterCount > 0 && (
                <Badge colorScheme="blue" borderRadius="full" px={3} py={1}>
                  {activeFilterCount} active {activeFilterCount === 1 ? 'filter' : 'filters'}
                </Badge>
              )}
            </div>
            <div className="flex items-center gap-3">
              {activeFilterCount > 0 && (
                <Button
                  size="sm"
                  variant="ghost"
                  colorScheme="red"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleClearFilters();
                  }}
                  leftIcon={<FiX />}
                >
                  Clear All
                </Button>
              )}
              <Icon 
                as={showAdvancedFilters ? FiX : FiFilter} 
                className={`text-xl transition-transform duration-300 ${
                  showAdvancedFilters ? 'rotate-180' : ''
                }`}
              />
            </div>
          </button>

          <Collapse in={showAdvancedFilters} animateOpacity>
            <Divider />
            <div className="p-6 bg-gray-50">
              <VStack spacing={6} align="stretch">
                {isRideCategory ? (
                  // Ride Sharing Filters
                  <>
                    {/* Route Filters */}
                    <Box>
                      <Flex alignItems="center" gap={2} mb={4}>
                        <FaRoute className="text-purple-600 text-xl" />
                        <Text fontSize="lg" fontWeight="semibold" color="gray.800">
                          Route Filters
                        </Text>
                      </Flex>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <FormControl>
                          <FormLabel fontWeight="medium" fontSize="sm">
                            From City
                          </FormLabel>
                          <Select
                            value={fromCity}
                            onChange={(e) => setFromCity(e.target.value)}
                            size="sm"
                            borderRadius="md"
                            placeholder="Any city"
                          >
                            <option value="">Any city</option>
                            {pakistanCities.map(city => (
                              <option key={city} value={city}>{city}</option>
                            ))}
                          </Select>
                        </FormControl>

                        <FormControl>
                          <FormLabel fontWeight="medium" fontSize="sm">
                            To City
                          </FormLabel>
                          <Select
                            value={toCity}
                            onChange={(e) => setToCity(e.target.value)}
                            size="sm"
                            borderRadius="md"
                            placeholder="Any city"
                          >
                            <option value="">Any city</option>
                            {pakistanCities.map(city => (
                              <option key={city} value={city}>{city}</option>
                            ))}
                          </Select>
                        </FormControl>
                      </div>
                    </Box>

                    {/* Ride Type and Schedule */}
                    <Box>
                      <Flex alignItems="center" gap={2} mb={4}>
                        <FaClock className="text-purple-600 text-xl" />
                        <Text fontSize="lg" fontWeight="semibold" color="gray.800">
                          Schedule & Type
                        </Text>
                      </Flex>
                      
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <FormControl>
                          <FormLabel fontWeight="medium" fontSize="sm">
                            Ride Type
                          </FormLabel>
                          <Select
                            value={rideType}
                            onChange={(e) => setRideType(e.target.value)}
                            size="sm"
                            borderRadius="md"
                            placeholder="Any type"
                          >
                            <option value="">Any type</option>
                            <option value="one-way">One Way</option>
                            <option value="round-trip">Round Trip</option>
                          </Select>
                        </FormControl>

                        <FormControl>
                          <FormLabel fontWeight="medium" fontSize="sm">
                            Departure Date
                          </FormLabel>
                          <Input
                            type="date"
                            value={departureDate}
                            onChange={(e) => setDepartureDate(e.target.value)}
                            size="sm"
                            borderRadius="md"
                          />
                        </FormControl>

                        <FormControl>
                          <FormLabel fontWeight="medium" fontSize="sm">
                            Min Available Seats
                          </FormLabel>
                          <Select
                            value={availableSeats}
                            onChange={(e) => setAvailableSeats(e.target.value)}
                            size="sm"
                            borderRadius="md"
                            placeholder="Any"
                          >
                            <option value="">Any</option>
                            <option value="1">1+ seats</option>
                            <option value="2">2+ seats</option>
                            <option value="3">3+ seats</option>
                            <option value="4">4+ seats</option>
                          </Select>
                        </FormControl>
                      </div>
                    </Box>

                    {/* Comfort & Preferences */}
                    <Box>
                      <Flex alignItems="center" gap={2} mb={4}>
                        <FaLuggageCart className="text-purple-600 text-xl" />
                        <Text fontSize="lg" fontWeight="semibold" color="gray.800">
                          Comfort & Preferences
                        </Text>
                      </Flex>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <FormControl>
                          <FormLabel fontWeight="medium" fontSize="sm">
                            Luggage Space
                          </FormLabel>
                          <Select
                            value={luggageSpace}
                            onChange={(e) => setLuggageSpace(e.target.value)}
                            size="sm"
                            borderRadius="md"
                            placeholder="Any"
                          >
                            <option value="">Any</option>
                            <option value="Small">Small (Carry-on only)</option>
                            <option value="Medium">Medium (1-2 bags)</option>
                            <option value="Large">Large (3+ bags)</option>
                          </Select>
                        </FormControl>

                        <FormControl display="flex" alignItems="center" justifyContent="space-between">
                          <FormLabel htmlFor="smoking" mb={0} fontWeight="medium">
                            No Smoking
                          </FormLabel>
                          <Switch
                            id="smoking"
                            colorScheme="purple"
                            isChecked={smoking}
                            onChange={(e) => setSmoking(e.target.checked)}
                          />
                        </FormControl>

                        <FormControl display="flex" alignItems="center" justifyContent="space-between">
                          <FormLabel htmlFor="pets" mb={0} fontWeight="medium">
                            Pets Allowed
                          </FormLabel>
                          <Switch
                            id="pets"
                            colorScheme="purple"
                            isChecked={pets}
                            onChange={(e) => setPets(e.target.checked)}
                          />
                        </FormControl>

                        <FormControl display="flex" alignItems="center" justifyContent="space-between">
                          <FormLabel htmlFor="femaleOnly" mb={0} fontWeight="medium">
                            Female Only
                          </FormLabel>
                          <Switch
                            id="femaleOnly"
                            colorScheme="pink"
                            isChecked={femaleOnly}
                            onChange={(e) => setFemaleOnly(e.target.checked)}
                          />
                        </FormControl>
                      </div>
                    </Box>
                  </>
                ) : (
                  // Sale/Rent Filters
                  <>
                    {/* Installment Options */}
                    <Box>
                      <Flex alignItems="center" gap={2} mb={4}>
                        <GiMoneyStack className="text-green-600 text-xl" />
                        <Text fontSize="lg" fontWeight="semibold" color="gray.800">
                          Installment Filters
                        </Text>
                      </Flex>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        <FormControl display="flex" alignItems="center" justifyContent="space-between">
                          <FormLabel htmlFor="installments" mb={0} fontWeight="medium">
                            Installments Available
                          </FormLabel>
                          <Switch
                            id="installments"
                            colorScheme="green"
                            isChecked={installmentsAvailable}
                            onChange={(e) => setInstallmentsAvailable(e.target.checked)}
                          />
                        </FormControl>

                        <FormControl>
                          <FormLabel fontWeight="medium" fontSize="sm">
                            Max Down Payment (PKR)
                          </FormLabel>
                          <Input
                            type="number"
                            placeholder="e.g., 500000"
                            value={maxDownPayment}
                            onChange={(e) => setMaxDownPayment(e.target.value)}
                            size="sm"
                            borderRadius="md"
                          />
                        </FormControl>

                        <FormControl>
                          <FormLabel fontWeight="medium" fontSize="sm">
                            Max Monthly Payment (PKR)
                          </FormLabel>
                          <Input
                            type="number"
                            placeholder="e.g., 50000"
                            value={maxMonthlyPayment}
                            onChange={(e) => setMaxMonthlyPayment(e.target.value)}
                            size="sm"
                            borderRadius="md"
                          />
                        </FormControl>

                        <FormControl>
                          <FormLabel fontWeight="medium" fontSize="sm">
                            Min Installment Period
                          </FormLabel>
                          <Select
                            value={minInstallmentPeriod}
                            onChange={(e) => setMinInstallmentPeriod(e.target.value)}
                            size="sm"
                            borderRadius="md"
                          >
                            <option value="">Any</option>
                            <option value="3">3 months</option>
                            <option value="6">6 months</option>
                            <option value="12">12 months</option>
                            <option value="24">24 months</option>
                            <option value="36">36 months</option>
                            <option value="48">48 months</option>
                            <option value="60">60 months</option>
                          </Select>
                        </FormControl>

                        <FormControl>
                          <FormLabel fontWeight="medium" fontSize="sm">
                            Max Installment Period
                          </FormLabel>
                          <Select
                            value={maxInstallmentPeriod}
                            onChange={(e) => setMaxInstallmentPeriod(e.target.value)}
                            size="sm"
                            borderRadius="md"
                          >
                            <option value="">Any</option>
                            <option value="12">12 months</option>
                            <option value="24">24 months</option>
                            <option value="36">36 months</option>
                            <option value="48">48 months</option>
                            <option value="60">60 months</option>
                            <option value="72">72 months</option>
                          </Select>
                        </FormControl>
                      </div>
                    </Box>

                    {/* Exchange Options */}
                    <Box>
                      <Flex alignItems="center" gap={2} mb={4}>
                        <FaExchangeAlt className="text-purple-600 text-xl" />
                        <Text fontSize="lg" fontWeight="semibold" color="gray.800">
                          Exchange Filters
                        </Text>
                      </Flex>

                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        <FormControl display="flex" alignItems="center" justifyContent="space-between">
                          <FormLabel htmlFor="exchange" mb={0} fontWeight="medium">
                            Exchange Available
                          </FormLabel>
                          <Switch
                            id="exchange"
                            colorScheme="purple"
                            isChecked={exchangeAvailable}
                            onChange={(e) => setExchangeAvailable(e.target.checked)}
                          />
                        </FormControl>

                        <FormControl>
                          <FormLabel fontWeight="medium" fontSize="sm">
                            Preferred Exchange Manufacturer
                          </FormLabel>
                          <Select
                            value={exchangeManufacturer}
                            onChange={(e) => setExchangeManufacturer(e.target.value)}
                            size="sm"
                            borderRadius="md"
                            placeholder="Select manufacturer"
                          >
                            <option value="">Any</option>
                            <option value="Toyota">Toyota</option>
                            <option value="Honda">Honda</option>
                            <option value="Suzuki">Suzuki</option>
                            <option value="Hyundai">Hyundai</option>
                            <option value="Kia">Kia</option>
                            <option value="Nissan">Nissan</option>
                          </Select>
                        </FormControl>

                        <FormControl>
                          <FormLabel fontWeight="medium" fontSize="sm">
                            Minimum Condition
                          </FormLabel>
                          <Select
                            value={exchangeCondition}
                            onChange={(e) => setExchangeCondition(e.target.value)}
                            size="sm"
                            borderRadius="md"
                            placeholder="Select condition"
                          >
                            <option value="">Any</option>
                            <option value="excellent">Excellent</option>
                            <option value="good">Good</option>
                            <option value="fair">Fair</option>
                          </Select>
                        </FormControl>
                      </div>
                    </Box>
                  </>
                )}

                {/* Active Filters Summary */}
                {activeFilterCount > 0 && (
                  <Box mt={4} p={4} bg="blue.50" borderRadius="lg">
                    <Flex alignItems="center" gap={3} flexWrap="wrap">
                      <Text fontWeight="medium" color="blue.800">
                        Active Filters:
                      </Text>
                      {carModel && (
                        <Tag colorScheme="blue" size="md" borderRadius="full">
                          <TagLabel>Model: {carModel}</TagLabel>
                          <TagCloseButton onClick={() => setCarModel("")} />
                        </Tag>
                      )}
                      {fromCity && (
                        <Tag colorScheme="purple" size="md" borderRadius="full">
                          <TagLabel>From: {fromCity}</TagLabel>
                          <TagCloseButton onClick={() => setFromCity("")} />
                        </Tag>
                      )}
                      {toCity && (
                        <Tag colorScheme="purple" size="md" borderRadius="full">
                          <TagLabel>To: {toCity}</TagLabel>
                          <TagCloseButton onClick={() => setToCity("")} />
                        </Tag>
                      )}
                      {rideType && (
                        <Tag colorScheme="pink" size="md" borderRadius="full">
                          <TagLabel>{rideType === "one-way" ? "One Way" : "Round Trip"}</TagLabel>
                          <TagCloseButton onClick={() => setRideType("")} />
                        </Tag>
                      )}
                      {departureDate && (
                        <Tag colorScheme="blue" size="md" borderRadius="full">
                          <TagLabel>Date: {departureDate}</TagLabel>
                          <TagCloseButton onClick={() => setDepartureDate("")} />
                        </Tag>
                      )}
                      {installmentsAvailable && (
                        <Tag colorScheme="green" size="md" borderRadius="full">
                          <TagLabel>Installments Available</TagLabel>
                          <TagCloseButton onClick={() => setInstallmentsAvailable(false)} />
                        </Tag>
                      )}
                      {exchangeAvailable && (
                        <Tag colorScheme="purple" size="md" borderRadius="full">
                          <TagLabel>Exchange Available</TagLabel>
                          <TagCloseButton onClick={() => setExchangeAvailable(false)} />
                        </Tag>
                      )}
                    </Flex>
                  </Box>
                )}
              </VStack>
            </div>
          </Collapse>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header Section */}
        <div className="mb-8">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6 mb-8">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-3">
                <div className={`p-3 rounded-xl bg-gradient-to-r ${getCategoryColor()} text-white`}>
                  {getCategoryIcon()}
                </div>
                <div>
                  <h1 className="text-3xl md:text-4xl font-bold text-gray-900">
                    {getCategoryTitle()}
                  </h1>
                  <p className="text-gray-600 mt-2">
                    {getCategoryDescription()}
                  </p>
                </div>
              </div>
            </div>

            {/* Stats Card */}
            <div className="bg-white rounded-2xl shadow-lg p-6 border border-blue-100">
              <div className="text-center">
                <div className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-sky-500 bg-clip-text text-transparent">
                  {displayCars.length}
                </div>
                <div className="text-sm text-gray-600">
                  {isRideCategory ? "Available Rides" : "Available Now"}
                </div>
              </div>
            </div>
          </div>

          {/* Results Count */}
          <div className="flex items-center justify-end mb-6">
            <div className="text-sm text-gray-600">
              Showing {displayCars.length} results
            </div>
          </div>
        </div>

        {/* Seller Type Filter - Only for Sale category */}
        {isSaleCategory && (
          <div className="mb-8">
            <div className="bg-white rounded-2xl shadow-sm border border-blue-100 p-4 mb-6">
              <h2 className="text-lg font-semibold text-gray-800 mb-4">
                Filter by Seller Type
              </h2>
              <div className="flex flex-wrap gap-3">
                {[
                  { id: "all", label: "All Cars", icon: <FiEye />, count: totalHomeUsed + totalShowroom },
                  { id: "home", label: "Home Used", icon: <FiHome />, count: totalHomeUsed },
                  { id: "showroom", label: "Showrooms", icon: <FiShoppingBag />, count: totalShowroom }
                ].map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => {
                      setListingType(tab.id);
                      setShowroomPage(1);
                    }}
                    className={`group relative flex items-center gap-3 px-6 py-3 rounded-xl font-semibold transition-all duration-300 ${
                      listingType === tab.id
                        ? `bg-gradient-to-r from-blue-600 to-sky-500 text-white shadow-lg`
                        : "bg-white text-gray-700 border border-gray-300 hover:border-blue-300 hover:shadow-md"
                    }`}
                  >
                    <div className={`${listingType === tab.id ? "text-white" : "text-gray-500 group-hover:text-blue-600"}`}>
                      {tab.icon}
                    </div>
                    <span>{tab.label}</span>
                    <span className={`px-2.5 py-1 text-xs rounded-full ${
                      listingType === tab.id ? "bg-white/30" : "bg-gray-100 group-hover:bg-blue-50"
                    }`}>
                      {tab.count}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Results Section - List View Only */}
        <div className="space-y-6">
          {displayCars.length > 0 ? (
            <>
              {/* Single Column List View */}
              <div className="space-y-4 md:mx-[10%]">
                {displayCars.map((car, index) => (
                  <div key={`${car.type || 'home'}-${car.id}-${index}`}>
                    <ListingItem
                      id={car.id}
                      data={car.data}
                      sellerId={car.data.sellerId}
                    />
                  </div>
                ))}
              </div>
            </>
          ) : (
            <Alert
              status="info"
              variant="subtle"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              textAlign="center"
              height="400px"
              borderRadius="2xl"
              className="bg-gradient-to-br from-blue-50 to-sky-50 border border-blue-200 shadow-lg"
            >
              <div className={`w-20 h-20 bg-gradient-to-r ${getCategoryColor()} rounded-full flex items-center justify-center mb-6`}>
                {getCategoryIcon()}
              </div>
              <AlertTitle mt={4} mb={1} fontSize="2xl" className="text-gray-800">
                No listings found
              </AlertTitle>
              <AlertDescription maxWidth="md" fontSize="lg" className="text-gray-600">
                {`We couldn't find any ${params.categoryName} listings matching your criteria.`}
                <div className="mt-8 space-x-4">
                  <Button
                    className="px-8 py-6 bg-gradient-to-r from-blue-500 to-sky-500 text-white font-semibold rounded-xl hover:from-blue-600 hover:to-sky-600 transition-all duration-300 hover:scale-105 shadow-lg"
                    onClick={handleClearFilters}
                  >
                    Clear All Filters
                  </Button>
                </div>
              </AlertDescription>
            </Alert>
          )}
        </div>
      </div>
    </div>
  );
};

export default Listings;